import React, { useState } from 'react';
import { Box, TextField, IconButton, Button, Stack } from '@mui/material';
import { DragIndicator, Delete, Add } from '@mui/icons-material';
import { EditItemProps } from '../../../../api/useNewItem';
import { CRMAttributeConfig } from '../../types';
import { SimpleDialog } from '../../../primitives';

interface Props {
  data: EditItemProps<CRMAttributeConfig & { idx: number }>;
}

interface LocalOption {
  _id: string;
  _isOriginal: boolean;
  value: string;
  label: string;
}

export const SelectOptionsConfigurationDialog = (props: Props) => {
  const { data } = props;

  const options: LocalOption[] = data.item?.select_options as any || [];
  const setOptions = (x: LocalOption[]) => data.update({ select_options: x });

  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIdx(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString()); // Обязательно для работы в Firefox
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    setDragOverIdx(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // Обязательно, чтобы разрешить drop
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === index) return;

    const newOptions = [...options];
    const [draggedItem] = newOptions.splice(draggedIdx, 1);
    newOptions.splice(index, 0, draggedItem);

    setOptions(newOptions);
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const handleDragEnd = () => {
    setDraggedIdx(null);
    setDragOverIdx(null);
  };


  const handleChange = (index: number, field: 'value' | 'label', val: string) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: val };
    setOptions(newOptions);
  };

  const handleAdd = () => {
    setOptions([
      ...options,
      {
        _id: Math.random().toString(36).substring(2, 9),
        _isOriginal: false,
        value: '',
        label: ''
      }
    ]);
  };

  const handleRemove = (index: number) => {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    setOptions(newOptions);
  };

  if (!data.item) {
    return null;
  }

  return (
    <SimpleDialog
      {...data.dialog}
      dialogTitle="Настройка вариантов"
      noFullscreen
      save={() => data.save()}
    >
      <Stack spacing={1.5} sx={{ mt: 1, minWidth: 400 }}>
        {options.map((opt, index) => {
          const isDragging = draggedIdx === index;
          const isDragOver = dragOverIdx === index;

          return (
            <Stack
              key={opt._id}
              direction="row"
              spacing={2}
              alignItems="center"
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              sx={{
                opacity: isDragging ? 0.4 : 1,
                // Визуальная подсказка (синяя линия) сверху или снизу элемента при наведении
                borderTop: isDragOver && draggedIdx !== null && draggedIdx > index ? '2px solid #cccccc' : '2px solid transparent',
                borderBottom: isDragOver && draggedIdx !== null && draggedIdx < index ? '2px solid #cccccc' : '2px solid transparent',
                transition: 'opacity 0.2s, border 0.2s',
                bgcolor: 'background.paper',
                p: 0.5,
                borderRadius: 0,
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <IconButton 
                disableRipple
                sx={{ cursor: isDragging ? 'grabbing' : 'grab' }} 
                size="small"
              >
                <DragIndicator color="action" />
              </IconButton>

              <TextField
                label="Value"
                size="small"
                value={opt.value}
                onChange={(e) => handleChange(index, 'value', e.target.value)}
                disabled={opt._isOriginal} // Запрещаем изменение старых value
                fullWidth
              />

              <TextField
                label="Label (отображаемое)"
                size="small"
                value={opt.label}
                onChange={(e) => handleChange(index, 'label', e.target.value)}
                fullWidth
              />

              <IconButton onClick={() => handleRemove(index)} color="error">
                <Delete />
              </IconButton>
            </Stack>
          );
        })}

        <Box sx={{ pt: 1 }}>
          <Button startIcon={<Add />} onClick={handleAdd} variant="outlined" size="small">
            Добавить вариант
          </Button>
        </Box>
      </Stack>
    </SimpleDialog>
  );
};
